package org.gymly

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.Preview
import coil3.ImageLoader
import coil3.compose.setSingletonImageLoaderFactory
import coil3.network.ktor3.KtorNetworkFetcherFactory
import org.gymly.auth.SessionIdleGuard
import org.gymly.auth.trackSessionActivity
import org.gymly.auth.WebSessionRestore
import org.gymly.canSwitchWebExperience
import org.gymly.loginUnificado
import org.gymly.data.AdminRepo
import org.gymly.data.PortalRepo
import org.gymly.data.PortalTermsGuard
import org.gymly.model.AdminUser
import org.gymly.model.AdminRoute
import org.gymly.model.LoginSuccess
import org.gymly.model.PortalTab
import org.gymly.model.PortalTerms
import org.gymly.model.PortalUser
import org.gymly.theme.GymlyTheme
import org.gymly.ui.admin.AdminApp
import org.gymly.ui.admin.AdminLoginScreen
import org.gymly.ui.screens.LoginScreen
import org.gymly.ui.screens.PortalPasswordGate
import org.gymly.ui.screens.PortalTermsGate
import org.gymly.ui.shell.PortalShell
import org.gymly.navigation.WebHistory
import org.gymly.navigation.currentWebHash
import org.gymly.navigation.pushWebHash
import org.gymly.navigation.rememberWebHistoryStateWithInitialReplace

@Composable
@Preview
fun App() {
    setSingletonImageLoaderFactory {
        ImageLoader.Builder(it)
            .components {
                add(
                    KtorNetworkFetcherFactory(
                        httpClient = {
                            when {
                                !AdminRepo.session.accessToken.isNullOrBlank() -> AdminRepo.imageHttpClient
                                else -> PortalRepo.imageHttpClient
                            }
                        },
                    ),
                )
            }
            .build()
    }

    GymlyTheme {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color.White),
        ) {
            when {
                // Web: un solo login (UI admin) → admin o portal según el usuario.
                loginUnificado() -> WebUnifiedApp()
                // Móvil: solo portal cliente.
                isPortalExperience() -> PortalApp()
                // Escritorio: solo panel admin.
                else -> AdminAppRoot()
            }
        }
    }
}

/**
 * Web: misma pantalla de login para personal y socios.
 * Auth: intenta staff (`/api/auth/login`) y, si falla, cliente (`/api/auth/client/login`).
 */
@Composable
private fun WebUnifiedApp() {
    var session by remember { mutableStateOf<LoginSuccess?>(null) }
    var sessionRestoreDone by remember { mutableStateOf(!loginUnificado()) }

    LaunchedEffect(Unit) {
        if (loginUnificado()) {
            session = WebSessionRestore.restore(currentWebHash())
            sessionRestoreDone = true
            notifyWebShellReady()
        }
    }

    SessionIdleGuard(
        active = sessionRestoreDone && session != null,
        onIdleTimeout = {
            when (session) {
                is LoginSuccess.Admin -> AdminRepo.api.logout()
                is LoginSuccess.Portal -> PortalRepo.api.logout()
                null -> Unit
            }
            session = null
            pushWebHash(WebHistory.loginHash(), replace = true)
        },
    )

    when {
        !sessionRestoreDone -> Unit
        session == null -> AdminLoginScreen(
            unified = true,
            onLoggedIn = { result ->
                session = result
                val hash = currentWebHash()
                when (result) {
                    is LoginSuccess.Portal -> {
                        if (!WebHistory.isPortalHash(hash)) {
                            pushWebHash(WebHistory.portalHash(PortalTab.Home), replace = true)
                        }
                    }
                    is LoginSuccess.Admin -> {
                        if (!WebHistory.isAdminHash(hash)) {
                            pushWebHash(WebHistory.adminHash(AdminRoute.Dashboard), replace = true)
                        }
                    }
                }
            },
        )
        else -> Box(Modifier.trackSessionActivity(active = true)) {
            when (val s = session!!) {
            is LoginSuccess.Admin -> AdminApp(
                user = s.user,
                onLogout = {
                    AdminRepo.api.logout()
                    session = null
                    pushWebHash(WebHistory.loginHash(), replace = true)
                },
            )
            is LoginSuccess.Portal -> WebPortalSession(
                user = s.user,
                onLogout = {
                    PortalRepo.api.logout()
                    session = null
                    pushWebHash(WebHistory.loginHash(), replace = true)
                },
            )
            }
        }
    }
}

@Composable
private fun WebPortalSession(
    user: PortalUser,
    onLogout: () -> Unit,
) {
    val (portalTab, setPortalTab) = rememberWebHistoryStateWithInitialReplace(
        initial = PortalTab.Home,
        hashFor = { WebHistory.portalHash(it) },
        parseFromHash = { WebHistory.parsePortalTab(it) },
    )
    // Sin aceptar los términos vigentes, el menú queda visible pero bloqueado: solo responde "Términos".
    // Si el gimnasio no exige términos (monitoreo apagado), no hay bloqueo ni pestaña "Términos".
    val revision by PortalTermsGuard.revision.collectAsState()
    var terms by remember { mutableStateOf<PortalTerms?>(null) }
    LaunchedEffect(revision) {
        terms = runCatching { PortalRepo.api.fetchTerms() }.getOrNull()
    }
    val t = terms
    val required = t?.required ?: true
    val locked = t == null || t.suspended || (required && !t.accepted)
    PortalShell(
        user = user,
        currentTab = if (locked) PortalTab.Terms else portalTab,
        onTabSelected = { if (!locked || it == PortalTab.Terms) setPortalTab(it) },
        onLogout = onLogout,
        navLocked = locked,
        termsVisible = required,
    )
}

/**
 * Portal del cliente/socio (móvil).
 * Auth: [PortalRepo] → `/api/auth/client/login`.
 */
@Composable
private fun PortalApp() {
    var user by remember { mutableStateOf<PortalUser?>(null) }
    var currentTab by remember { mutableStateOf(PortalTab.Home) }

    SessionIdleGuard(
        active = user != null,
        onIdleTimeout = {
            PortalRepo.api.logout()
            user = null
        },
    )

    if (user == null) {
        LoginScreen(onLoggedIn = { loggedIn ->
            user = loggedIn
            currentTab = PortalTab.Home
        })
    } else {
        Box(Modifier.trackSessionActivity(active = true)) {
            // El socio acepta los términos vigentes antes de entrar.
            PortalTermsGate(onLogout = {
                PortalRepo.api.logout()
                user = null
            }) {
                // Con contraseña temporal, primero debe elegir una propia.
                PortalPasswordGate(onLogout = {
                    PortalRepo.api.logout()
                    user = null
                }) {
                    PortalShell(
                        user = user!!,
                        currentTab = currentTab,
                        onTabSelected = { currentTab = it },
                        onLogout = {
                            PortalRepo.api.logout()
                            user = null
                        },
                    )
                }
            }
        }
    }
}

/**
 * Panel admin (escritorio). Auth: [AdminRepo] → `/api/auth/login`.
 */
@Composable
private fun AdminAppRoot() {
    var user by remember { mutableStateOf<AdminUser?>(null) }

    SessionIdleGuard(
        active = user != null,
        onIdleTimeout = {
            AdminRepo.api.logout()
            user = null
        },
    )

    if (user == null) {
        AdminLoginScreen(
            unified = false,
            onLoggedIn = { result ->
                if (result is LoginSuccess.Admin) user = result.user
            },
        )
    } else {
        Box(Modifier.trackSessionActivity(active = true)) {
            AdminApp(
                user = user!!,
                onLogout = {
                    AdminRepo.api.logout()
                    user = null
                },
            )
        }
    }
}
